import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { usePersonForm } from "../../data/persons/usePersonForm";
import { ButtonProps, Form, Button, Divider, Header, Message, Segment, SelectProps } from "semantic-ui-react";
import { IPersonFormData } from "../../models/IPerson";
import { CompanySelect } from "../companies/CompanySelect";
import PersonCertForm from "./PersonCertForm";
import { IPersonCert } from "../../models/IPersonCert";
import { RenderCert } from "./RenderCert";
import { PersonCertStatus } from "./PersonCertStatus";
import { getPersonCertStatus } from "../../utils/cert.helper";

interface PersonFormProps {
    initialData?: IPersonFormData;
    onSubmit: (values: IPersonFormData) => void;
    onCancel: () => void;
    loading?: boolean;
    requiredCerts?: Array<string>;
    disabledPersonalDetail?: boolean;
    formError?: string;
    inputForCertStartDate?: boolean;
};

const PersonForm: React.FC<PersonFormProps> = ({
    initialData,
    onSubmit,
    onCancel,
    loading = false,
    disabledPersonalDetail = false,
    requiredCerts,
    formError,
    inputForCertStartDate = true
}) => {
    const t = useTranslation().t;

    const texts = {
        save: t("Tallenna"),
        name: t("Nimi"),
        cancel: t("Peruuta"),
        language: t("Kieli"),
        firstname: t("Etunimi"),
        surname: t("Sukunimi"),
        phone: t("Puhelinnumero"),
        email: t("Sähköposti"),
        company: t("Yritys"),
        addImage: t("Lisää kuva"),

        notificationByEmail: t("Ilmoitukset (email)"),
        emailNotificationInfo: t("hpr_user_will_recieve_notifications_by_email"),

        notificationByEmailOrSms: t("Ilmoitukset (SMS tai email)"),
        emailOrSmsNotificationInfo: t("hpr_user_will_recieve_notifications_by_sms_or_email"),

        permitsRequiredForWork: t("Työhön vaaditut pätevyydet"),
        missingConfigurationForCert: t("Konfigurointi puuttuu pätevyydelle")
    };

    const { formData, update, validate, errors } = usePersonForm(initialData);

    const handleSubmit: ButtonProps["onClick"] = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const hasErrors = validate(formData, requiredCerts);

        if (hasErrors) return;

        onSubmit(formData);
    };

    const makeInputChange = (key: keyof IPersonFormData) => (_1: any, { value }: { value: any }) => {
        return update({ [key]: value })
    };

    const updateCompany = (value?: number) => {
        return update({ company: value })
    };

    const updateCert = (key: string, data: IPersonCert) => {
        return update({
            certs: {
                ...(formData.certs || {}),
                [key]: data
            }
        })
    };

    const updateLang: SelectProps["onChange"] = (_1, { value }) => {
        return update({ lang: value as any })
    };



    return (
        <Form>

            <Form.Input
                error={errors.firstname}
                label={texts.firstname}
                value={formData.firstname}
                onChange={makeInputChange("firstname")}
                disabled={disabledPersonalDetail}
            />

            <Form.Input
                error={errors.surname}
                label={texts.surname}
                value={formData.surname}
                onChange={makeInputChange("surname")}
                disabled={disabledPersonalDetail}
            />


            <Form.Input
                error={errors.phone}
                label={texts.phone}
                value={formData.phone}
                onChange={makeInputChange("phone")}
                disabled={disabledPersonalDetail}
            />

            <Form.Input
                error={errors.email}
                label={texts.email}
                value={formData.email}
                onChange={makeInputChange("email")}
                disabled={disabledPersonalDetail}
            />

            <Form.Field >
                <label> {texts.company} </label>
                <CompanySelect
                    value={formData.company}
                    onChange={updateCompany}

                />
            </Form.Field>

            <Form.Select
                onChange={updateLang}
                label={texts.language}
                value={formData.lang}
                options={[
                    { text: "Suomi", value: "fi" },
                    { text: "Englanti", value: "us" },
                    { text: "Ruotsi", value: "se" }
                ]}
            />

            {requiredCerts && requiredCerts.length ? (
                <Fragment>
                    <Header> {texts.permitsRequiredForWork} </Header>
                    {requiredCerts.map((key) => (
                        <RenderCert
                            referenceName={key}
                            render={({ cert }) => {

                                if (!cert) {
                                    return <Message negative> {texts.missingConfigurationForCert} {key}  </Message>
                                }
                                const personCertData = (initialData?.certs && initialData.certs[key]);
                                const personCertFormData = (formData?.certs && formData.certs[key]);
                                const certErrors = errors.certs && (errors as any).certs[key];

                                return (
                                    <Segment>
                                        {personCertData ? <PersonCertStatus status={getPersonCertStatus(personCertData, cert.isTimeLimited)} /> : undefined}
                                        <PersonCertForm
                                            cert={cert}
                                            errors={certErrors}
                                            initialData={personCertFormData ? {
                                                ...personCertFormData,
                                                valid: !cert.isTimeLimited ? personCertFormData.valid : undefined
                                            } : undefined}
                                            inputForCertStartDate={inputForCertStartDate}
                                            onChange={(values) => updateCert(key, values as any)}
                                        />
                                    </Segment>
                                )
                            }}
                        />

                    ))}
                </Fragment>
            ) : null}

            <Divider hidden />
            {formError && (
                <Message content={formError} negative />
            )}
            <Divider hidden />

            <Button
                type="button"
                loading={loading}
                primary
                onClick={handleSubmit}
                content={texts.save}
            />
            <Button
                type="button"
                onClick={onCancel}
                content={texts.cancel}
            />
        </Form>
    );
};


export default PersonForm;
